import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { supabase } from "../lib/supabaseClient";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const PAYMENT_AMOUNT_EUR = 500;

const localeFor = (language) => {
  if (language?.startsWith("de")) return "de-DE";
  if (language?.startsWith("en")) return "en-GB";
  return "fr-FR";
};

const CheckoutForm = ({ proposalDetails }) => {
  const { t, i18n } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const locale = localeFor(i18n.resolvedLanguage || i18n.language);
  const formattedAmount = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(PAYMENT_AMOUNT_EUR);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    if (!stripe || !elements) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch("/api/create-payment-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: PAYMENT_AMOUNT_EUR * 100,
          proposalId: proposalDetails.proposalId,
          proposalType: proposalDetails.proposalType,
        }),
      });

      if (!response.ok) throw new Error(t("museumProposalPayment.errors.createPayment"));

      const data = await response.json();
      const { paymentIntent, error: stripeError } = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (stripeError) throw new Error(stripeError.message || t("museumProposalPayment.errors.payment"));

      if (paymentIntent.status === "succeeded") {
        await supabase
          .from("proposals")
          .update({
            status: "paid",
            payment_id: paymentIntent.id,
            payment_amount: paymentIntent.amount,
            payment_date: new Date().toISOString(),
          })
          .eq("id", proposalDetails.proposalId);

        navigate("/payment-success", {
          state: { proposalDetails, paymentId: paymentIntent.id },
        });
      }
    } catch (caughtError) {
      console.error("Payment error:", caughtError);
      setError(caughtError.message || t("museumProposalPayment.errors.payment"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="detail-container-artsy">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#424770",
                "::placeholder": { color: "#aab7c4" },
                fontFamily: "var(--font-sans)",
              },
              invalid: { color: "#9e2146" },
            },
          }}
        />
      </div>

      {error && <div className="p-3 bg-red-50 text-red-700 rounded-md">{error}</div>}

      <button
        type="submit"
        disabled={!stripe || isLoading}
        className={`btn-artsy btn-primary-artsy w-full ${(!stripe || isLoading) ? "opacity-70 cursor-not-allowed" : ""}`}
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <span className="animate-spin h-5 w-5 mr-3 border-t-2 border-b-2 border-white rounded-full" />
            {t("museumProposalPayment.checkout.processing")}
          </span>
        ) : (
          t("museumProposalPayment.checkout.pay", { amount: formattedAmount })
        )}
      </button>
    </form>
  );
};

export const MuseumProposalPayment = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [proposalDetails, setProposalDetails] = useState(null);
  const locale = localeFor(i18n.resolvedLanguage || i18n.language);
  const formattedAmount = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(PAYMENT_AMOUNT_EUR);

  useEffect(() => {
    if (location.state?.proposalId) setProposalDetails(location.state);
    else navigate("/propose-museum");
  }, [location, navigate]);

  if (!proposalDetails) {
    return (
      <div className="flex justify-center items-center h-screen" aria-label={t("museumProposalPayment.loading")}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-black" />
      </div>
    );
  }

  const isAuction = proposalDetails.proposalType === "auction";
  const expertName = isAuction ? t("museumProposalPayment.partner.team") : "Madeleine Filippi";
  const serviceKey = isAuction ? "auction" : "institution";

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-3xl title-artsy mb-6">{t(`museumProposalPayment.title.${serviceKey}`)}</h1>

      <div className="detail-container-artsy mb-8">
        <h2 className="text-xl title-artsy mb-4">{t("museumProposalPayment.summary.title")}</h2>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">{t("museumProposalPayment.summary.artwork")}</span>
            <span className="font-medium">{proposalDetails.artworkTitle || t("common.untitled")}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{t(`museumProposalPayment.summary.target.${serviceKey}`)}</span>
            <span className="font-medium">{proposalDetails.institution}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{t("museumProposalPayment.summary.service")}</span>
            <span className="font-medium">{t(`museumProposalPayment.summary.serviceName.${serviceKey}`)}</span>
          </div>
          <div className="border-t pt-3">
            <div className="flex justify-between">
              <span className="font-medium">{t("museumProposalPayment.summary.initialAmount")}</span>
              <span className="font-medium">{t("museumProposalPayment.summary.amountExVat", { amount: formattedAmount })}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="detail-container-artsy mb-8">
        <h2 className="text-xl title-artsy mb-4">{t("museumProposalPayment.service.title")}</h2>
        <div className="space-y-4 mb-6">
          <div>
            <h3 className="font-medium">{t("museumProposalPayment.service.partner", { name: expertName })}</h3>
            <p className="text-gray-600">{t(`museumProposalPayment.service.partnerDescription.${serviceKey}`)}</p>
          </div>

          <div className="badge-loaned p-4 rounded-md">
            <h3 className="font-medium text-blue-800 mb-2">{t("museumProposalPayment.service.includesTitle")}</h3>
            <ul className="list-disc pl-5 text-gray-700 space-y-1">
              {[1, 2, 3, 4, 5].map((index) => (
                <li key={index}>{t(`museumProposalPayment.service.includes.${serviceKey}.${index}`)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-medium">{t("museumProposalPayment.pricing.title")}</h3>
            <ul className="text-gray-700 space-y-1">
              <li>{t("museumProposalPayment.pricing.fixed", { amount: formattedAmount })}</li>
              <li>{t("museumProposalPayment.pricing.hourly")}</li>
              <li>{t(`museumProposalPayment.pricing.commission.${serviceKey}`)}</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="detail-container-artsy mb-8">
        <h2 className="text-xl title-artsy mb-4">{t("museumProposalPayment.paymentTitle")}</h2>
        <Elements stripe={stripePromise}>
          <CheckoutForm proposalDetails={proposalDetails} />
        </Elements>
      </div>

      <div className="text-xs text-gray-500 mt-4">
        <p>
          <Trans
            i18nKey="museumProposalPayment.legal.acceptance"
            components={{
              // eslint-disable-next-line jsx-a11y/anchor-has-content -- contenu injecté par <Trans>
              terms: <a href="/conditions-generales" className="link-effect" />,
            }}
          />
        </p>
        <p>{t("museumProposalPayment.legal.additionalInvoices")}</p>
      </div>
    </div>
  );
};
